import { useCallback, useState } from "react";

/**
 * State for tracking async operations
 */
export interface AsyncState<T = unknown> {
  isLoading: boolean;
  error: Error | null;
  data: T | null;
}

/**
 * Hook for managing loading states with error handling
 *
 * @example
 * const { isLoading, error, execute } = useAsyncState();
 *
 * const handleSubmit = async () => {
 *   const result = await execute(
 *     api.submitBooking(formData),
 *     "Failed to submit booking"
 *   );
 *   if (result) {
 *     // Handle success
 *   }
 * };
 */
export function useAsyncState<T = unknown>() {
  const [state, setState] = useState<AsyncState<T>>({
    isLoading: false,
    error: null,
    data: null
  });

  /**
   * Execute an async operation with automatic loading/error state management
   * @param promise - The promise to execute
   * @param errorMessage - Optional custom error message
   * @returns The result of the promise, or null if an error occurred
   */
  const execute = useCallback(
    async (promise: Promise<T>, errorMessage?: string): Promise<T | null> => {
      setState({ isLoading: true, error: null, data: null });

      try {
        const data = await promise;
        setState({ isLoading: false, error: null, data });
        return data;
      } catch (error) {
        const errorObj =
          error instanceof Error
            ? error
            : new Error(errorMessage || "An unexpected error occurred");

        setState({ isLoading: false, error: errorObj, data: null });
        console.error("AsyncState error:", errorObj);
        return null;
      }
    },
    []
  );

  /**
   * Reset the state to initial values
   */
  const reset = useCallback(() => {
    setState({ isLoading: false, error: null, data: null });
  }, []);

  return {
    ...state,
    execute,
    reset
  };
}

/**
 * Hook for managing multiple loading states
 * Useful when you have several independent async operations
 *
 * @example
 * const { isLoading, startLoading, stopLoading } = useLoadingState();
 * const departmentsLoading = startLoading("departments");
 * // ... fetch departments
 * stopLoading("departments");
 */
export function useLoadingState() {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  const startLoading = useCallback((key: string) => {
    setLoadingStates(prev => ({ ...prev, [key]: true }));
  }, []);

  const stopLoading = useCallback((key: string) => {
    setLoadingStates(prev => ({ ...prev, [key]: false }));
  }, []);

  const isLoading = useCallback(
    (key?: string) => {
      if (key) {
        return loadingStates[key] === true;
      }
      return Object.values(loadingStates).some(loading => loading);
    },
    [loadingStates]
  );

  const reset = useCallback(() => {
    setLoadingStates({});
  }, []);

  return {
    isLoading,
    startLoading,
    stopLoading,
    reset,
    loadingStates
  };
}
